import React, { Suspense, useEffect } from 'react';
import { useLocation } from 'react-router-dom';

import { init } from '@common/analytics';
import { useIsConfigurationLoaded } from '@common/configuration';
import { muiProLicenseInit } from '@common/mui-pro-license';
import { UserPilotProvider } from '@common/hooks/use-userpilot';
import SuspendRender from '@common/components/suspend-render/suspend-render';
import InlineLoadingIndicator from '@common/components/loading-indicator/inline-loading-indicator';
import { useSession } from '@common/contexts/session';

import { OnlineTestingBundle, TestEditorBundle } from '@aware/bundles';
import { PUBLIC_ITEM_BANK, ONLINE_TESTING_ROUTE } from '@routes';

import V2Home from '@aware/pages/v2-home/v2-home';

const App = () => {
  const { pathname } = useLocation();
  const isOnlineTesting = pathname
    .toLowerCase()
    .includes(ONLINE_TESTING_ROUTE.toLowerCase());
  const { currentUser, rights } = useSession();
  const configurationLoaded = useIsConfigurationLoaded('aware');

  const prereqsLoaded = configurationLoaded && currentUser;

  // Once currentUser info is loaded, initialize Google Analytics
  useEffect(() => {
    if (currentUser) {
      init(isOnlineTesting ? 'onlineTesting' : 'aware', currentUser);
    }
  }, [currentUser]); // eslint-disable-line

  muiProLicenseInit();

  // The Chrome plugin Co:Writer messes with the Roboto font, so revert to system-fonts for question text when present
  if (
    document.querySelector('dji-cowriter') &&
    document.body.className.indexOf('force-arial') === -1
  ) {
    document.body.className += ' force-arial';
  }

  return (
    <Suspense fallback={<InlineLoadingIndicator />}>
      <SuspendRender
        isLoading={!prereqsLoaded}
        loadingComponent={<InlineLoadingIndicator />}
      >
        <UserPilotProvider currentUser={currentUser} rights={rights}>
          {isOnlineTesting ? (
            <OnlineTestingBundle />
          ) : pathname.toLowerCase().startsWith(PUBLIC_ITEM_BANK.toLowerCase()) ? (
            <TestEditorBundle publicItemBank />
          ) : (
            <V2Home />
          )}
        </UserPilotProvider>
      </SuspendRender>
    </Suspense>
  );
};

export default App;
